import { useFormContext } from 'react-hook-form';
import { useMemo } from 'react';
import { Segment } from 'api/services/availability/availability-service.dto';
import { DateTime } from 'luxon';
import { MonthAvailabilityQueryResult } from './month-availability-query.types';
import { SelectedDateWithAvailability } from '../wizard/availability-sidebar';
import { SegmentsForEdit, TimeSlotFormContext } from '../wizard/time-slot.types';

const mapQuerySegmentToSegmentForEdit = (segment: Segment): SegmentsForEdit => ({
  segmentId: segment.segmentId,
  utcStartDateTime: DateTime.fromISO(segment.startTime).toFormat('T'),
  utcEndDateTime: DateTime.fromISO(segment.endTime).toFormat('T'),
});

const getDaySegmentsForKind = (
  dayAvailability:
    | {
        availableSegments: Segment[];
        unavailableSegments: Segment[];
      }
    | undefined,
  segmentKind: TimeSlotFormContext['segmentKind']
): Segment[] => {
  if (segmentKind === 'unavailable') {
    return dayAvailability?.unavailableSegments ?? [];
  }

  return dayAvailability?.availableSegments ?? [];
};

export const useGetAdditionalAvailabilities = ({
  selectedDates,
  monthAvailabilityQuery,
}: {
  selectedDates: SelectedDateWithAvailability[];
  monthAvailabilityQuery: MonthAvailabilityQueryResult;
}) => {
  const { watch } = useFormContext<TimeSlotFormContext>();
  const selectedWorkerId = watch('workerSelect');
  const segmentKind = watch('segmentKind');

  const availabilitiesForAllSelectedDays: SegmentsForEdit[] | undefined = useMemo(() => {
    if (monthAvailabilityQuery.status !== 'success') {
      return undefined;
    }

    return selectedDates.flatMap((selectedDate) => {
      const formattedDateKey = selectedDate.date.toFormat('dd-MM-yyyy');
      const dayAvailability = monthAvailabilityQuery.data?.availabilities[formattedDateKey];
      const daySegments = getDaySegmentsForKind(dayAvailability, segmentKind);

      return daySegments.map(mapQuerySegmentToSegmentForEdit);
    });
  }, [
    monthAvailabilityQuery.data,
    monthAvailabilityQuery.status,
    selectedDates,
    selectedWorkerId,
    segmentKind,
  ]);

  return { availabilitiesForAllSelectedDays };
};
